/**
 * The application shell.
 *
 * **The chrome is shared and whole** (FiveOX ADR-0037, constitution §7,
 * ADR-0023). `<PlatformShell>` is a top bar, a left sidebar and the content
 * between them; this app renders it and composes nothing of its own around
 * it. In particular:
 *
 * - The nav bar carries **no app navigation**. It is suite chrome: brand, org,
 *   notifications, app-switcher waffle, account menu.
 * - ProGST's navigation goes in the **sidebar, as data** — `NavItem[]` with at
 *   most one level of `children`.
 * - The bell is the shell's, mounted with **our** authenticated fetcher, not a
 *   token: each app owns a different session store with its own idea of what a
 *   dead session means, and the bell must not become one more place that
 *   decides.
 * - **No router import inside the package.** `renderLink` wraps each item in
 *   react-router's `<NavLink>`; without one the sidebar renders plain anchors.
 *
 * Between 2026-09-04 and 2026-09-09 this file rendered a ProGST-owned
 * `LegacyShell` instead (ADR-0021). That is over: the frame is the platform's
 * again, and the four things the fork dropped — the waffle, the bell, the
 * account menu, the org control — come back with the one component.
 */

import {
  NoAccess,
  Notifications,
  PlatformShell,
  navItemClass,
  signOutEverywhere,
  subNavItemClass,
  useSwitcherApps,
} from '@fiveox/shell'
import type { NavItem } from '@fiveox/shell'
import {
  Building2,
  CalendarDays,
  ClipboardList,
  Download as DownloadIcon,
  FileSpreadsheet,
  History,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Send,
  Scale,
  Table2,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router'

import { CLIENT, useEvaluation } from './lib/access'
import { CLIENT_SCREENS, type ClientScreen, clientPath, useClientId } from './lib/clientScope'
import type { Verb } from './lib/access'
import { getOrgId, setOrgId } from './lib/api'
import { useOrgs } from './lib/use-orgs'
import {
  ACCESS_API,
  ACCOUNTS_URL,
  ADMIN_URL,
  APP_NAME,
  APP_SLUG,
  APPMGMT_API,
  AUTH_API,
  COMM_API,
  CONSOLE_URL,
  CRM_URL,
  HELP_URL,
  LAUNCHER_URL,
  STORE_URL,
  SUPPORT_URL,
} from './lib/config'
import { ORG_SCREEN_NEEDS, SCREEN_NEEDS } from './lib/screenAccess'
import { useMe } from './lib/use-me'
import { session } from './lib/session'
import { Paywall } from './routes/Paywall'

/**
 * The sidebar, as data.
 *
 * `needs` is ours, not the shell's: `[resource, verb]`, matching the matrix
 * access-management actually speaks. An item whose verb the user does not hold
 * is **dropped**, not disabled — a disabled item advertises a capability the
 * user cannot obtain and generates a support ticket.
 *
 * **The labels are the practitioner's vocabulary** — "Upload Data", "Raw Data",
 * "GSTR-1", "GSTR-3B" are what they navigate by, and vocabulary is content, not
 * chrome (ADR-0023). The route paths stay ours, because a URL is not muscle
 * memory.
 */
const NAV: Array<NavItem & { needs?: readonly [string, Verb] }> = [
  { key: 'overview', label: 'Overview', href: '/', icon: <LayoutDashboard size={18} />, end: true },
  {
    key: 'clients',
    label: 'Clients',
    href: '/clients',
    icon: <Building2 size={18} />,
    // `end`, because a prefix match lights this up on every `/clients/<id>/…`
    // path as well as the item the user is actually on. Two highlighted items
    // is what got the old "Switch Client"/"Client Settings" pair deleted on
    // 2026-09-05; nesting brings the same trap back.
    end: true,
    needs: [CLIENT, 'can_view'],
  },
  {
    // Org-level, beside Clients (FR-E7): every client's due dates on one screen. Not a client
    // screen, so it is not in `CLIENT_SCREENS` and keeps its own path whichever client is open.
    key: 'calendar',
    label: 'Calendar',
    href: '/calendar',
    icon: <CalendarDays size={18} />,
    needs: ORG_SCREEN_NEEDS['calendar'],
  },
  {
    key: 'intake',
    label: 'Upload Data',
    href: '/intake',
    icon: <Inbox size={18} />,
    needs: SCREEN_NEEDS['intake'],
  },
  {
    key: 'review',
    label: 'Raw Data',
    href: '/review',
    icon: <ListChecks size={18} />,
    needs: SCREEN_NEEDS['review'],
  },
  {
    key: 'returns',
    label: 'GSTR-1',
    href: '/returns',
    icon: <FileSpreadsheet size={18} />,
    needs: SCREEN_NEEDS['returns'],
  },
  {
    key: 'gstr3b',
    label: 'GSTR-3B',
    href: '/gstr3b',
    icon: <Table2 size={18} />,
    needs: SCREEN_NEEDS['gstr3b'],
  },
  {
    key: 'reconciliation',
    label: 'Reconciliation',
    href: '/reconciliation',
    // `recon`, as RBAC v2 actually named it — the placeholder said
    // `reconciliation` and would have filtered the item out forever.
    icon: <Scale size={18} />,
    needs: SCREEN_NEEDS['reconciliation'],
  },
  {
    key: 'filed-compare',
    label: 'Prepared vs Filed',
    href: '/filed-compare',
    icon: <Scale size={18} />,
    needs: SCREEN_NEEDS['filed-compare'],
  },
  {
    key: 'tb-recon',
    label: 'Books vs Return',
    href: '/tb-recon',
    icon: <Scale size={18} />,
    needs: SCREEN_NEEDS['tb-recon'],
  },
  {
    key: 'reports',
    label: 'Reports',
    href: '/reports',
    icon: <ClipboardList size={18} />,
    needs: SCREEN_NEEDS['reports'],
  },
  {
    key: 'download',
    label: 'Download',
    href: '/download',
    icon: <DownloadIcon size={18} />,
    needs: SCREEN_NEEDS['download'],
  },
  {
    key: 'activity',
    label: 'Activity',
    href: '/activity',
    icon: <History size={18} />,
    // `client.can_view`, deliberately the same permission as the client record
    // itself. This is a list of what happened to that client's work, and a
    // second authorization vocabulary for it is the shadow-authorization
    // mistake rbac.py warns about.
    needs: SCREEN_NEEDS['activity'],
  },
  {
    // Wave D (D-05): the GST portal session for this client. Built and OFF until the lane's
    // credentials are preflighted; the screen says why in one sentence.
    key: 'filing',
    label: 'Filing',
    href: '/filing',
    icon: <Send size={18} />,
    needs: SCREEN_NEEDS['filing'],
  },
]

export function App({ children }: { children: ReactNode }) {
  const current = session.useSession()
  const location = useLocation()
  const me = useMe()
  // Resolve the org *before* anything asks for permissions: every request to
  // gst-service carries it, and `useEvaluation` is one of them.
  const { orgs, selected, loading: orgsLoading, failed: orgsFailed } = useOrgs()
  const { evaluation, loading, can } = useEvaluation()
  // ND-C1: the URL says which client every client-owned screen is about. This
  // reads it; it does not decide it.
  const clientId = useClientId()

  // **The waffle is identity-shaped, not app-shaped** (ADR-0054): an internal
  // caller is offered the internal apps their platform-scoped assignments
  // grant; an external caller gets the customer estate. One resolver, all apps.
  //
  // `appmgmtApiUrl` + `orgId` are what put the org's own products in it: the
  // shell reads `GET /api/apps/me/apps?org=…` and renders only the rows the
  // server marks `launchable`. `fetcher` rather than a bare token, for the
  // reason the bell takes one: it refreshes a spent access token and replays,
  // and this app's session store is the only thing that knows what a dead
  // session means here.
  const switcherApps = useSwitcherApps({
    accessApiUrl: ACCESS_API,
    accessToken: current?.accessToken ?? null,
    staffUrls: { 'core-admin': ADMIN_URL, 'core-crm': CRM_URL, 'core-support': SUPPORT_URL },
    platformUrls: {
      accounts: ACCOUNTS_URL,
      console: CONSOLE_URL,
      store: STORE_URL,
      help: HELP_URL,
    },
    accountsUrl: ACCOUNTS_URL,
    appmgmtApiUrl: APPMGMT_API,
    // Null until `useOrgs` resolves, which is a real state on first paint. The
    // shell holds the empty list until then rather than reading the wrong org.
    orgId: selected?.id ?? null,
    fetcher: session.authed,
  })

  const signOut = () => {
    void signOutEverywhere({
      authApiUrl: AUTH_API,
      refreshToken: current?.refreshToken ?? null,
      clearLocal: session.clearSession,
    })
  }

  if (orgsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading your organisations…</p>
      </div>
    )
  }

  // Org-service did not answer. **Not** the same as belonging to no org, and
  // the copy must not say it is — telling someone they have no organisation
  // when the service is merely down sends them to support with the wrong
  // question.
  if (orgsFailed) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="We can’t reach your organisations"
        message="The organisation service did not answer. This is usually brief — please try again in a moment."
      />
    )
  }

  // Genuinely a member of nothing. A real state for a brand-new account, and
  // the only way out is to create or join one — which is accounts' job, not
  // ours.
  if (!selected || !getOrgId()) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="You’re not in an organisation yet"
        message="ProGST works inside an organisation — that is who holds the subscription and the client list. Create one from your account, then come back."
      />
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking your access…</p>
      </div>
    )
  }

  // An empty verb set is the single answer for every lapse — never bought,
  // subscription suspended, app killed, authority unreachable. Enforcing
  // `permissions` alone is entitlement-correct (guide §7.3), and the *reason*
  // is what lets the screen say why.
  if (!evaluation || Object.keys(evaluation.permissions).length === 0) {
    return (
      <Paywall
        reason={evaluation?.entitlementReason ?? 'authority_unreachable'}
        orgs={orgs}
        selectedOrgId={selected.id}
        onSelectOrg={setOrgId}
      />
    )
  }

  // A client-owned entry points inside the current client. With no client in
  // the URL there is no id to point at, so it keeps its OLD FLAT PATH — which
  // the router redirects to the picker (B-02/B-06).
  //
  // Not `/clients` directly: nine items all pointing at `/clients` light the
  // entire sidebar up the moment you are on the client list. The flat path
  // matches nothing, so nothing is falsely current, and the click still lands
  // on the picker.
  const scoped = new Set<string>(CLIENT_SCREENS)
  const items: NavItem[] = NAV.filter(
    (item) => !item.needs || can(item.needs[0], item.needs[1]),
  ).map(({ needs: _needs, ...item }) =>
    scoped.has(item.key)
      ? {
          ...item,
          href: clientId ? clientPath(clientId, item.key as ClientScreen) : `/${item.key}`,
        }
      : item,
  )

  // The client's own profile, reachable from the nav only once there is a
  // client to point at.
  if (clientId && can(CLIENT, 'can_view')) {
    const at = items.findIndex((i) => i.key === 'clients')
    items.splice(at + 1, 0, {
      key: 'client-profile',
      label: 'Client Details',
      href: clientPath(clientId),
      icon: <Building2 size={18} />,
      // Same reason: `/clients/<id>/returns` starts with `/clients/<id>`.
      end: true,
    })
  }

  // The org the firm is working as. In the practice shape (ADR-0002) this is
  // the CA firm, not the taxpayer — so the bar's org control carries the firm,
  // and the client whose books are open is the content's to show
  // (`ClientLayout`), never the chrome's.
  return (
    <PlatformShell
      appName={APP_NAME}
      appSlug={APP_SLUG}
      launcherUrl={LAUNCHER_URL}
      account={{
        name: me?.name ?? null,
        email: me?.email ?? null,
        pictureUrl: me?.pictureUrl ?? null,
        accountsUrl: ACCOUNTS_URL,
        onSignOut: signOut,
      }}
      apps={switcherApps}
      // Display-only, and the bar's alone (ADR-0062 §4): a staff account holds
      // no organisation, so the bar drops the org control for one rather than
      // each host deciding. Never a permission input — that is `useEvaluation`.
      accessToken={current?.accessToken ?? null}
      // The org switcher. One prop, two behaviours: with `options` and
      // `onSelect` it switches; with only `selected` it renders a read-only
      // chip — which is what a single-org user gets, correctly.
      org={{
        selected: { id: selected.id, display_name: selected.display_name, role: selected.role },
        options: orgs.length > 1 ? orgs : undefined,
        onSelect: orgs.length > 1 ? (id: string) => setOrgId(id) : undefined,
      }}
      notifications={<Notifications commApiUrl={COMM_API} fetcher={session.authed} />}
      nav={{
        items,
        currentPath: location.pathname,
        // The router decides "current", so the sidebar's inactive class is
        // replaced by the shell's own active/inactive pair — the canonical
        // look, with `end` honoured where an item asks for an exact match.
        renderLink: (item, _className, depth) => (
          <NavLink
            to={item.href}
            end={item.end}
            className={({ isActive }) =>
              depth === 1 ? subNavItemClass(isActive) : navItemClass(isActive)
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ),
      }}
      contentClassName="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6"
    >
      {children}
    </PlatformShell>
  )
}
